import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FiBook } from "react-icons/fi";
import {
  CalenderIcon,
  ChevronDownIcon,
  GridIcon,
  HorizontaLDots,
  // PieChartIcon,
  UserCircleIcon,
  TaskIcon,
  GroupIcon,
  ChatIcon,
} from "../icons";
import { useSidebar } from "../../context/SidebarContext";
import { useAuth } from "../../context/AuthContext";
import { vibrateTactile } from "../../helpers/vibrate";
import logo from "../../assets/LogoCabecalho.png";
import logoWhite from "../../assets/LogoCabecalhoBranca.png";
import ic1 from "../../assets/ic1.png";
import ic2 from "../../assets/ic2.png";

type NavItem = {
  name: string;
  icon: React.ReactNode;
  path?: string;
  subItems?: { name: string; path: string; pro?: boolean; new?: boolean }[];
  adminOnly?: boolean;
  staffOnly?: boolean;
};

const navItems: NavItem[] = [
  {
    icon: <CalenderIcon />,
    name: "Agenda",
    path: "/appointments",
  },
  {
    icon: <ChatIcon />,
    name: "Minhas Conversas",
    path: "/conversations",
  },
  {
    icon: <GroupIcon />,
    name: "Alunos",
    path: "/students",
  },
  {
    icon: <GridIcon />,
    name: "Dashboard",
    path: "/dashboard",
  },

  {
    icon: <TaskIcon />,
    name: "Exercícios",
    path: "/exercises",
  },
  // {
  //   icon: <PieChartIcon />,
  //   name: "Relatórios",
  //   path: "/reports",
  // },

  {
    icon: <UserCircleIcon />,
    name: "Perfil",
    path: "/perfil",
  },
  // {
  //   icon: <FiCreditCard />,
  //   name: "Pagamento",
  //   path: "/pagamento",
  // },

  {
    icon: <FiBook />,
    name: "Manual do Sistema",
    path: "/documentacoes",
  },

  {
    icon: <GridIcon />,
    name: "Dashboard Admin",
    path: "/admin/overview",
    adminOnly: true,
  },
  {
    icon: <GridIcon />,
    name: "Classificação de Exercícios",
    path: "/classificacao",
    staffOnly: true,
  },
];

const othersItems: NavItem[] = [];

const SWIPE_CLOSE_THRESHOLD = 60;

const AppSidebar: React.FC = () => {
  const {
    isExpanded,
    isMobileOpen,
    isHovered,
    setIsHovered,
    toggleMobileSidebar,
  } = useSidebar();
  const location = useLocation();
  const { getUser } = useAuth();
  const user = getUser();
  const isAdmin = !!user?.is_admin;
  const isStaff = !!user?.is_staff;

  const touchStartX = useRef<number>(0);
  const touchStartY = useRef<number>(0);

  const handleSidebarTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleSidebarTouchEnd = (e: React.TouchEvent) => {
    if (!isMobileOpen) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    const deltaY = e.changedTouches[0].clientY - touchStartY.current;
    if (Math.abs(deltaY) > Math.abs(deltaX)) return;
    if (deltaX < -SWIPE_CLOSE_THRESHOLD) {
      toggleMobileSidebar();
    }
  };

  const [openSubmenu, setOpenSubmenu] = useState<{
    type: "main" | "others";
    index: number;
  } | null>(null);
  const [subMenuHeight, setSubMenuHeight] = useState<Record<string, number>>(
    {},
  );
  const subMenuRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const isActive = useCallback(
    (path: string) => location.pathname === path,
    [location.pathname],
  );

  useEffect(() => {
    let submenuMatched = false;
    (["main", "others"] as const).forEach((menuType) => {
      const items = menuType === "main" ? navItems : othersItems;
      items.forEach((nav, index) => {
        nav.subItems?.forEach((subItem) => {
          if (isActive(subItem.path)) {
            setOpenSubmenu({ type: menuType, index });
            submenuMatched = true;
          }
        });
      });
    });

    if (!submenuMatched) {
      setOpenSubmenu(null);
    }
  }, [isActive, location.pathname]);

  useEffect(() => {
    if (openSubmenu === null) return;
    const key = `${openSubmenu.type}-${openSubmenu.index}`;
    const node = subMenuRefs.current[key];
    if (!node) return;
    setSubMenuHeight((prev) => ({ ...prev, [key]: node.scrollHeight || 0 }));
  }, [openSubmenu]);

  const handleSubmenuToggle = (index: number, menuType: "main" | "others") => {
    vibrateTactile();
    setOpenSubmenu((prev) => {
      if (prev && prev.type === menuType && prev.index === index) {
        return null;
      }
      return { type: menuType, index };
    });
  };

  const handleMobileMenuClose = () => {
    vibrateTactile();
    if (window.innerWidth < 768 && isMobileOpen) {
      toggleMobileSidebar();
    }
  };

  const renderMenuItems = (
    items: NavItem[],
    menuType: "main" | "others",
    isAdmin: boolean,
    isStaff: boolean,
  ) => (
    <ul className="flex flex-col gap-4">
      {items
        .filter(
          (nav) =>
            (!nav.adminOnly || isAdmin) && (!nav.staffOnly || isStaff),
        )
        .map((nav, index) => (
          <li key={nav.name}>
            {nav.subItems ? (
              <button
                onClick={() => handleSubmenuToggle(index, menuType)}
                className={`menu-item group cursor-pointer ${
                  openSubmenu?.type === menuType && openSubmenu?.index === index
                    ? "menu-item-active"
                    : "menu-item-inactive"
                } ${
                  !isExpanded && !isHovered
                    ? "lg:justify-center"
                    : "lg:justify-start"
                }`}
                aria-expanded={
                  openSubmenu?.type === menuType && openSubmenu?.index === index
                }
              >
                <span
                  className={`menu-item-icon-size ${
                    openSubmenu?.type === menuType &&
                    openSubmenu?.index === index
                      ? "menu-item-icon-active"
                      : "menu-item-icon-inactive"
                  }`}
                >
                  {nav.icon}
                </span>
                {(isExpanded || isHovered || isMobileOpen) && (
                  <span className="menu-item-text">{nav.name}</span>
                )}
                {(isExpanded || isHovered || isMobileOpen) && (
                  <ChevronDownIcon
                    className={`ml-auto h-5 w-5 transition-transform duration-200 ${
                      openSubmenu?.type === menuType &&
                      openSubmenu?.index === index
                        ? "rotate-180 text-brand-500"
                        : ""
                    }`}
                  />
                )}
              </button>
            ) : (
              nav.path && (
                <Link
                  to={nav.path}
                  onClick={handleMobileMenuClose}
                  className={`menu-item group ${
                    isActive(nav.path)
                      ? "menu-item-active"
                      : "menu-item-inactive"
                  }`}
                >
                  <span
                    className={`menu-item-icon-size ${
                      isActive(nav.path)
                        ? "menu-item-icon-active"
                        : "menu-item-icon-inactive"
                    }`}
                  >
                    {nav.icon}
                  </span>
                  {(isExpanded || isHovered || isMobileOpen) && (
                    <span className="menu-item-text">{nav.name}</span>
                  )}
                </Link>
              )
            )}

            {nav.subItems && (isExpanded || isHovered || isMobileOpen) && (
              <div
                ref={(el) => {
                  subMenuRefs.current[`${menuType}-${index}`] = el;
                }}
                className="overflow-hidden transition-all duration-300"
                style={{
                  height:
                    openSubmenu?.type === menuType &&
                    openSubmenu?.index === index
                      ? `${subMenuHeight[`${menuType}-${index}`]}px`
                      : "0px",
                }}
              >
                <ul className="ml-9 mt-2 space-y-1">
                  {nav.subItems.map((subItem) => (
                    <li key={subItem.name}>
                      <Link
                        to={subItem.path}
                        onClick={handleMobileMenuClose}
                        className={`menu-dropdown-item ${
                          isActive(subItem.path)
                            ? "menu-dropdown-item-active"
                            : "menu-dropdown-item-inactive"
                        }`}
                      >
                        {subItem.name}
                        <span className="ml-auto flex items-center gap-1">
                          {subItem.new && (
                            <span
                              className={`menu-dropdown-badge ${
                                isActive(subItem.path)
                                  ? "menu-dropdown-badge-active"
                                  : "menu-dropdown-badge-inactive"
                              }`}
                            >
                              new
                            </span>
                          )}
                          {subItem.pro && (
                            <span
                              className={`menu-dropdown-badge ${
                                isActive(subItem.path)
                                  ? "menu-dropdown-badge-active"
                                  : "menu-dropdown-badge-inactive"
                              }`}
                            >
                              pro
                            </span>
                          )}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </li>
        ))}
    </ul>
  );

  return (
    <aside
      className={`fixed left-0 top-0 z-50 mt-[60px] flex h-screen flex-col border-r border-gray-200 bg-white px-5 text-gray-900 transition-all duration-300 ease-in-out dark:border-gray-800 dark:bg-gray-900 lg:mt-0 ${
        isExpanded || isMobileOpen
          ? "w-[290px]"
          : isHovered
            ? "w-[290px]"
            : "w-[90px]"
      } ${
        isMobileOpen ? "translate-x-0" : "-translate-x-full"
      } lg:translate-x-0`}
      onMouseEnter={() => !isExpanded && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={handleSidebarTouchStart}
      onTouchEnd={handleSidebarTouchEnd}
    >
      <div
        className={`flex py-8 ${
          !isExpanded && !isHovered ? "lg:justify-center" : "justify-start"
        } hidden lg:flex`}
      >
        <Link to="/">
          {isExpanded || isHovered || isMobileOpen ? (
            <>
              <img
                src={logo}
                alt="Logo"
                width={150}
                height={40}
                className="dark:hidden"
              />
              <img
                src={logoWhite}
                alt="Logo"
                width={150}
                height={40}
                className="hidden dark:block"
              />
            </>
          ) : (
            <>
              <img
                src={ic2}
                alt="Logo"
                width={32}
                height={32}
                className="dark:hidden"
                style={{ objectFit: "cover", width: 32, height: 32 }}
              />
              <img
                src={ic1}
                alt="Logo"
                width={32}
                height={32}
                className="hidden dark:block"
                style={{ objectFit: "cover", width: 32, height: 32 }}
              />
            </>
          )}
        </Link>
      </div>

      <div className="no-scrollbar flex flex-1 flex-col overflow-y-auto duration-300 ease-linear">
        <nav className="mb-6">
          <div className="flex flex-col gap-4">
            <div className="mt-2">
              <h2
                className={`mb-4 flex text-xs uppercase leading-[20px] text-gray-400 ${
                  !isExpanded && !isHovered
                    ? "lg:justify-center"
                    : "justify-start"
                }`}
              >
                {isExpanded || isHovered || isMobileOpen ? (
                  "Menu"
                ) : (
                  <HorizontaLDots className="h-8 w-8" />
                )}
              </h2>
              {renderMenuItems(navItems, "main", isAdmin, isStaff)}
            </div>
          </div>
        </nav>
      </div>

      <div className="mb-6 flex flex-col gap-4">
        <div className="my-2 h-px w-full bg-gray-200 dark:bg-gray-800"></div>
        {renderMenuItems(othersItems, "others", isAdmin, isStaff)}
      </div>
    </aside>
  );
};

export default AppSidebar;
